/**
 * Room connection: a reconnecting WebSocket with snapshot/state/me handling, a server-clock offset
 * for accurate countdowns, and an event bus for transient events (reactions, reveals, toasts).
 */
import type { ClientMessage, PrivateState, PublicState, ServerEvent, ServerMessage } from "@shared/protocol";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed" | "kicked" | "ended";

export interface RoomHandle {
  state: PublicState | null;
  me: PrivateState | null;
  status: ConnectionStatus;
  /** Estimated `Date.now()` on the server. */
  serverNow: () => number;
  send: (msg: ClientMessage) => void;
  onEvent: (fn: (e: ServerEvent) => void) => () => void;
  /** Force an immediate reconnect attempt (e.g. after the tab becomes visible). */
  reconnect: () => void;
}

const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 15000];

export function useRoom(wsPath: string | null): RoomHandle {
  const [state, setState] = useState<PublicState | null>(null);
  const [me, setMe] = useState<PrivateState | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const offsetRef = useRef(0);
  const listeners = useRef(new Set<(e: ServerEvent) => void>());
  const versionRef = useRef(0);
  const stoppedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!wsPath) return;
    stoppedRef.current = false;
    attemptRef.current = 0;
    versionRef.current = 0;

    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${scheme}//${location.host}${wsPath}`;

    const connect = () => {
      if (stoppedRef.current) return;
      setStatus((s) => (s === "connecting" ? "connecting" : "reconnecting"));
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus("open");
        ws.send(JSON.stringify({ type: "ping", t: Date.now() } satisfies ClientMessage));
      };
      ws.onmessage = (ev) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(ev.data)) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case "snapshot":
            offsetRef.current = msg.state.serverNow - Date.now();
            versionRef.current = msg.state.version;
            setState(msg.state);
            setMe(msg.me);
            break;
          case "state":
            if (msg.state.version < versionRef.current) return; // stale
            versionRef.current = msg.state.version;
            offsetRef.current = msg.state.serverNow - Date.now();
            setState(msg.state);
            break;
          case "me":
            setMe(msg.me);
            break;
          case "pong": {
            const rtt = Date.now() - msg.t;
            offsetRef.current = msg.serverNow + rtt / 2 - Date.now();
            break;
          }
          case "event":
            if (msg.event.type === "kicked") {
              stoppedRef.current = true;
              setStatus("kicked");
            }
            if (msg.event.type === "ended") setStatus("ended");
            if (msg.event.type === "votesProgress") {
              // With live tallies hidden the room sends this instead of a full state; patch the open
              // window so progress lines and the voted-dots keep moving.
              const ev = msg.event;
              setState((cur) => {
                if (!cur?.vote || cur.vote.result) return cur;
                const voted = new Set(cur.vote.votedPlayerIds);
                for (const id of ev.voterIds ?? []) voted.add(id);
                return {
                  ...cur,
                  vote: {
                    ...cur.vote,
                    votesCast: ev.votesCast,
                    eligible: ev.eligible,
                    votedPlayerIds: [...voted],
                  },
                };
              });
            }
            for (const l of listeners.current) l(msg.event);
            break;
          case "error":
            for (const l of listeners.current) {
              l({
                type: "toast",
                level: "error",
                key: `error.${msg.code}`,
                params: { message: msg.message },
              });
            }
            break;
        }
      };
      ws.onclose = (ev) => {
        wsRef.current = null;
        if (stoppedRef.current) return;
        if (ev.code === 4001) {
          stoppedRef.current = true;
          setStatus("kicked");
          return;
        }
        setStatus("reconnecting");
        const delay =
          BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)]! * (0.8 + Math.random() * 0.4);
        attemptRef.current++;
        timerRef.current = window.setTimeout(connect, delay);
      };
      ws.onerror = () => {
        /* onclose follows */
      };
    };
    connect();

    // Reconnect promptly when the phone wakes up.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !wsRef.current && !stoppedRef.current) {
        if (timerRef.current) clearTimeout(timerRef.current);
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);

    // Application-level heartbeat keeps clocks aligned; the server auto-responds while hibernating.
    const heartbeat = window.setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
    }, 25_000);

    return () => {
      stoppedRef.current = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
      clearInterval(heartbeat);
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close(1000, "navigate");
      wsRef.current = null;
    };
  }, [wsPath]);

  return useMemo<RoomHandle>(
    () => ({
      state,
      me,
      status,
      serverNow: () => Date.now() + offsetRef.current,
      send: (msg) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
      },
      onEvent: (fn) => {
        listeners.current.add(fn);
        return () => {
          listeners.current.delete(fn);
        };
      },
      reconnect: () => {
        wsRef.current?.close();
      },
    }),
    [state, me, status],
  );
}

/** Ticks every `ms` while `active`; returns the current server time. */
export function useServerClock(room: Pick<RoomHandle, "serverNow">, active: boolean, ms = 250): number {
  const [now, setNow] = useState(() => room.serverNow());
  useEffect(() => {
    if (!active) return;
    setNow(room.serverNow());
    const id = window.setInterval(() => setNow(room.serverNow()), ms);
    return () => clearInterval(id);
  }, [active, ms, room]);
  return now;
}
